/**
 * RealTimeStatsDashboard - Lett versjon av Live Aktivitet
 *
 * Viser kun statistikk + aktivitetsstrøm i sanntid uten reol-grid eller heatmap.
 * Egnet for nettbrett og lavtytende enheter siden den unngår tunge DOM-strukturer
 * (ingen reol-celler, ingen animasjoner, ingen rack-API-kall).
 *
 * Gjenbruker:
 * - useLocationActivity (SignalR)
 * - ActivityCacheManager
 * - QuickStatsPanel
 * - ActivityStreamPanel
 * - TopLocationsCompact
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Activity, AlertCircle, X } from 'lucide-react';
import { ConnectionStatus } from '../../../common/ConnectionStatus';
import { BackButton } from '../../../common/BackButton';
import { QuickStatsPanel } from '../rack-builder-live/QuickStatsPanel';
import { ActivityStreamPanel } from '../rack-builder-live/ActivityStreamPanel';
import { TopLocationsCompact } from '../rack-builder-live/TopLocationsCompact';
import { ActivityCacheManager, TimeWindow } from '../rack-builder-live/ActivityCacheManager';
import { useLocationActivity } from '../../../../hooks/useLocationActivity';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import {
  LocationActivityEvent,
  ConnectionStatus as ConnectionStatusEnum,
} from '../../../../types/locationActivity';

interface RealTimeStatsDashboardProps {
  onBack?: () => void;
}

export const RealTimeStatsDashboard: React.FC<RealTimeStatsDashboardProps> = ({ onBack }) => {
  const { currentWarehouse } = useWarehouse();

  const [error, setError] = useState<string | null>(null);
  const [eventVersion, setEventVersion] = useState(0); // bumps on new events to trigger memo recompute

  // Cache manager - persistent event store
  const cacheManagerRef = useRef<ActivityCacheManager | null>(null);

  // Initialize cache manager once
  if (!cacheManagerRef.current) {
    cacheManagerRef.current = new ActivityCacheManager({
      maxCacheSize: 10000,
      enablePersistence: true,
      enableMidnightReset: true,
    });
  }

  /**
   * Handle new activity event from SignalR
   */
  const handleActivityReceived = useCallback((event: LocationActivityEvent): void => {
    if (cacheManagerRef.current) {
      cacheManagerRef.current.addEvent(event);
      setEventVersion(v => v + 1);
    }
  }, []);

  // SignalR connection
  const {
    connectionStatus,
    connectionQuality,
    joinWarehouseGroup,
  } = useLocationActivity(
    handleActivityReceived,
    handleConnectionStateChanged
  );

  /**
   * Handle connection state changes - join warehouse group on connect
   */
  function handleConnectionStateChanged(status: ConnectionStatusEnum): void {
    if (status === ConnectionStatusEnum.Connected) {
      setError(null);
      const defaultWarehouseId = currentWarehouse || 'LAGER01';
      joinWarehouseGroup(defaultWarehouseId).catch(err => {
        console.error('[RealTimeStats] Failed to join warehouse group:', err);
        setError('Kunne ikke koble til lagergruppe');
      });
    } else if (status === ConnectionStatusEnum.Disconnected) {
      setError('Mistet tilkobling til sanntidsdata. Prøver å koble til på nytt...');
    }
  }

  // Cleanup cache on unmount
  useEffect(() => {
    return () => {
      cacheManagerRef.current?.cleanup?.();
    };
  }, []);

  /**
   * Get all cached events for components - recomputes on each new event
   */
  const allCachedEvents = useMemo(() => {
    if (!cacheManagerRef.current) return [];
    return cacheManagerRef.current.getAllEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventVersion]);

  /**
   * Recent events for activity stream (last 50, most recent first)
   */
  const recentEvents = useMemo(() => {
    return allCachedEvents.slice(-50).reverse();
  }, [allCachedEvents]);

  /**
   * Empty queue stats - we don't manage a queue in light mode
   */
  const emptyQueueStats = useMemo(() => ({
    queueLength: 0,
    uniqueRacks: 0,
    totalEvents: allCachedEvents.length,
    avgEventsPerRack: 0,
  }), [allCachedEvents.length]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <BackButton onClick={onBack || (() => window.history.back())} />

          <div className="flex items-center gap-3">
            <Activity className="w-7 h-7 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Sanntidsstatistikk</h1>
              <p className="text-sm text-gray-600">Live aktivitetsstrøm og nøkkeltall</p>
            </div>
          </div>

          <ConnectionStatus
            status={connectionStatus}
            quality={connectionQuality}
            className="ml-6"
          />
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content - 2-Column Layout */}
      <div className="flex-1 overflow-hidden bg-gray-100 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] xl:grid-cols-[400px_1fr] gap-4 h-full overflow-hidden">

          {/* Left - Top Locations */}
          <aside className="flex flex-col gap-4 overflow-y-auto">
            <TopLocationsCompact
              events={allCachedEvents}
              timeWindow={TimeWindow.Today}
              topCount={10}
              onLocationClick={(binCode) => {
                console.log('[RealTimeStats] Location clicked:', binCode);
              }}
            />
          </aside>

          {/* Right - Stats + Activity Stream */}
          <main className="flex flex-col gap-4 overflow-hidden">
            <QuickStatsPanel
              allEvents={allCachedEvents}
              queueStats={emptyQueueStats}
              currentRack={null}
              currentWarehouseId={currentWarehouse || null}
            />

            <div className="flex-1 min-h-0 overflow-hidden">
              <ActivityStreamPanel
                events={recentEvents}
                maxEvents={50}
                autoScroll={true}
                onEventClick={(event) => {
                  console.log('[RealTimeStats] Event clicked:', event);
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default RealTimeStatsDashboard;
